import { styled } from '@mui/material/styles';
import WdPageSimple from '@main/core/WdPageSimple/WdPageSimple';
import { useContext, useEffect, useState } from 'react';
import { ComposedHeader, ComposedTags, FormCardLayout, TextWithLabel } from '@main/components';
import { getCompanyById } from 'src/app/api/company';
import { useRequest } from '@main/hooks';
import { useSearchParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { Box, Divider, Grid } from '@mui/material';
import { SnippetCopier } from 'app/components';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/core/messageSlice';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { formatDate } from 'app/utils/dates';

const Root = styled(WdPageSimple)(({ theme }) => ({
  backgroundColor: '#FFF',
  '& ?.WdPageSimple-header': {},
  '& ?.WdPageSimple-content': {},
}));

const companyTabsScheme = [
  {
    label: 'General',
    key: 'general',
  },
  {
    label: 'Plesk',
    key: 'plesk',
  },
  {
    label: 'Domains',
    key: 'apidomain',
  },
  {
    label: 'Services',
    key: 'managerdomain',
  },
  {
    label: 'Database',
    key: 'database',
  },
  {
    label: 'GitHub',
    key: 'github',
  },
  {
    label: 'Token',
    key: 'token',
  },
];

export const CompanieInfo = () => {
  const Dispatch = useDispatch();

  const { finalizeSession } = useContext(BootstrapContext);
  const [searchParams] = useSearchParams();
  const previousId = searchParams?.get('id');
  const [data, setData] = useState({});

  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const {
    makeRequest: tryGetCompany,
    response: responseGetCompany,
    isLoading: isLoadingGetCompany,
  } = useRequest(getCompanyById, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });

  useEffect(() => {
    if (previousId === undefined) return;
    tryGetCompany(previousId);
  }, [previousId]);

  useEffect(() => {
    if (responseGetCompany === undefined) return;
    setData(responseGetCompany);
    console?.log('Contenido de la respuesta', responseGetCompany);
  }, [responseGetCompany]);

  return (
    <Root
      header={
        <ComposedHeader
          title="Informacion de compañia"
          description="Información general de compañia"
          decorator="button"
        />
      }
      content={
        <Box
          className="flex justify-center items-start"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <FormCardLayout title="Informacion de compania" maxWidth="120rem">
            <ComposedTags
              tabs={companyTabsScheme}
              color="secondary"
              variant="fullWidth"
              fontSize="1.6rem"
            >
              {/* Tab 0: General */}
              {Object?.keys(data)?.length > 0 && (
                <ComposedTags.Element index={0}>
                  <FormCardLayout.FullColum>
                    {data?.key && <TextWithLabel title="Key de Empresa">{data?.key}</TextWithLabel>}
                    {data?.step && <TextWithLabel title="Paso actual">{data?.step}</TextWithLabel>}
                    {data?.name && (
                      <TextWithLabel title="Nombre de Empresa">{data?.name}</TextWithLabel>
                    )}
                    {data?.email && (
                      <TextWithLabel title="Email Empresarial">{data?.email}</TextWithLabel>
                    )}
                    {data?.status !== undefined && (
                      <TextWithLabel title="Estado">
                        {data?.status ? 'Activo' : 'Inactivo'}
                      </TextWithLabel>
                    )}
                    <TextWithLabel title="Fecha de creación">
                      {formatDate(data?.created_at)}
                    </TextWithLabel>
                  </FormCardLayout.FullColum>
                </ComposedTags.Element>
              )}

              {/* Tab 1: Plesk */}
              {data?.plesk && Object?.keys(data?.plesk)?.length > 0 && (
                <ComposedTags.Element index={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Divider textAlign="left" className="my-16">
                        <Typography className="text-2xl font-light">Suscripción</Typography>
                      </Divider>
                      {data?.plesk?.domain && (
                        <TextWithLabel title="Dominio">{data?.plesk?.domain}</TextWithLabel>
                      )}
                      {data?.plesk?.servicePlan && (
                        <TextWithLabel title="Plan Plesk">{data?.plesk?.servicePlan}</TextWithLabel>
                      )}
                      {data?.plesk?.connection && (
                        <TextWithLabel title="Conexión Key">
                          {data?.plesk?.connection}
                        </TextWithLabel>
                      )}
                      {data?.plesk?.type && (
                        <TextWithLabel title="Tipo de Hosting">{data?.plesk?.type}</TextWithLabel>
                      )}
                      {data?.plesk?.ip && (
                        <TextWithLabel title="IP">{data?.plesk?.ip}</TextWithLabel>
                      )}
                      {data?.plesk?.baseUri && (
                        <TextWithLabel title="Api URL">{data?.plesk?.baseUri}</TextWithLabel>
                      )}
                      {data?.plesk?.key && (
                        <TextWithLabel title="Api Key">{data?.plesk?.key}</TextWithLabel>
                      )}
                    </Grid>
                    <Grid item xs={12} md={1} className="flex justify-center items-center">
                      <Divider orientation="vertical" flexItem />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Divider textAlign="left" className="my-16">
                        <Typography className="text-2xl font-light">Cuenta</Typography>
                      </Divider>
                      {data?.plesk?.owner && (
                        <TextWithLabel title="Usuario">{data?.plesk?.login}</TextWithLabel>
                      )}
                      {data?.plesk?.passwd && (
                        <TextWithLabel title="Constrasena">{data?.plesk?.passwd}</TextWithLabel>
                      )}
                    </Grid>
                  </Grid>
                </ComposedTags.Element>
              )}

              {/* Tab 2: Domains/Services/Notify/Websocket/Manager */}
              {(data?.apiDomain ||
                data?.terminalsDomain ||
                data?.notifyDomain ||
                data?.managerDomain) && (
                <ComposedTags.Element index={2}>
                  <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ flex: 1 }}>
                        {data?.apiDomain && Object?.keys(data?.apiDomain)?.length > 0 && (
                          <>
                            <Divider textAlign="left" className="my-16">
                              <Typography className="text-2xl font-light">Api Domain</Typography>
                            </Divider>
                            {data?.apiDomain?.parent && (
                              <TextWithLabel title="Dominio">{data?.apiDomain?.name}</TextWithLabel>
                            )}
                            {data?.apiDomain?.envPath && (
                              <TextWithLabel title="Config path">
                                {data?.apiDomain?.envPath}
                              </TextWithLabel>
                            )}
                            {data?.apiDomain?.pathRoot && (
                              <TextWithLabel title="Path root">
                                {data?.apiDomain?.pathRoot}
                              </TextWithLabel>
                            )}
                          </>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {data?.terminalsDomain &&
                          Object?.keys(data?.terminalsDomain)?.length > 0 && (
                            <>
                              <Divider textAlign="left" className="my-16">
                                <Typography className="text-2xl font-light">
                                  Websocket Domain
                                </Typography>
                              </Divider>
                              {data?.terminalsDomain?.name && (
                                <TextWithLabel title="Dominio">
                                  {data?.terminalsDomain?.name}
                                </TextWithLabel>
                              )}
                              {data?.terminalsDomain?.port && (
                                <TextWithLabel title="Puerto Socket IO">
                                  {data?.terminalsDomain?.port}
                                </TextWithLabel>
                              )}
                              {data?.terminalsDomain?.portWebsocket && (
                                <TextWithLabel title="Puerto Websocket">
                                  {data?.terminalsDomain?.portWebsocket}
                                </TextWithLabel>
                              )}
                              {data?.terminalsDomain?.envWebsocketPath && (
                                <TextWithLabel title="Config path">
                                  {data?.terminalsDomain?.envWebsocketPath}
                                </TextWithLabel>
                              )}
                              {data?.terminalsDomain?.pathRoot && (
                                <TextWithLabel title="Path root">
                                  {data?.terminalsDomain?.pathRoot}
                                </TextWithLabel>
                              )}
                            </>
                          )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={1} className="flex justify-center items-stretch my-24">
                      <Divider orientation="vertical" flexItem />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ flex: 1 }}>
                        {data?.notifyDomain && Object?.keys(data?.notifyDomain)?.length > 0 && (
                          <>
                            <Divider textAlign="left" className="my-16">
                              <Typography className="text-2xl font-light">Notify Domain</Typography>
                            </Divider>
                            {data?.notifyDomain?.name && (
                              <TextWithLabel title="Dominio">
                                {data?.notifyDomain?.name}
                              </TextWithLabel>
                            )}
                            {data?.notifyDomain?.port && (
                              <TextWithLabel title="Puerto">
                                {data?.notifyDomain?.port}
                              </TextWithLabel>
                            )}
                            {data?.notifyDomain?.envNotifyPath && (
                              <TextWithLabel title="Config path">
                                {data?.notifyDomain?.envNotifyPath}
                              </TextWithLabel>
                            )}
                            {data?.notifyDomain?.pathRoot && (
                              <TextWithLabel title="Path root">
                                {data?.notifyDomain?.pathRoot}
                              </TextWithLabel>
                            )}
                          </>
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {data?.managerDomain && Object?.keys(data?.managerDomain)?.length > 0 && (
                          <>
                            <Divider textAlign="left" className="my-16">
                              <Typography className="text-2xl font-light">
                                Manager Domain
                              </Typography>
                            </Divider>
                            {data?.managerDomain?.name && (
                              <TextWithLabel title="Dominio">
                                {data?.managerDomain?.name}
                              </TextWithLabel>
                            )}
                            {data?.managerDomain?.envManagerPath && (
                              <TextWithLabel title="Config path">
                                {data?.managerDomain?.envManagerPath}
                              </TextWithLabel>
                            )}
                            {data?.managerDomain?.pathRoot && (
                              <TextWithLabel title="Path root">
                                {data?.managerDomain?.pathRoot}
                              </TextWithLabel>
                            )}
                            {data?.managerAdmin?.name && (
                              <TextWithLabel title="Usuario">
                                {data?.managerAdmin?.name}
                              </TextWithLabel>
                            )}
                            {data?.managerAdmin?.email && (
                              <TextWithLabel title="Correo">
                                {data?.managerAdmin?.email}
                              </TextWithLabel>
                            )}
                            {data?.managerAdmin?.password && (
                              <TextWithLabel title="Contraseña">
                                {data?.managerAdmin?.password}
                              </TextWithLabel>
                            )}
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </ComposedTags.Element>
              )}

              {/* Tab 3: Face/Fingerprint Service Api */}
              {(data?.faceServer || data?.fingerprintServer || data?.mqtt) && (
                <ComposedTags.Element index={3}>
                  <Grid container spacing={2}>
                    {data?.faceServer && Object?.keys(data?.faceServer)?.length > 0 && (
                      <Grid item xs={12} md={5}>
                        <Divider textAlign="left" className="my-16">
                          <Typography className="text-2xl font-light">Face Service Api</Typography>
                        </Divider>
                        {data?.faceServer?.name && (
                          <TextWithLabel title="Nombre">{data?.faceServer?.name}</TextWithLabel>
                        )}
                        {data?.faceServer?.version && (
                          <TextWithLabel title="Versión">{data?.faceServer?.version}</TextWithLabel>
                        )}
                        {data?.faceServer?.config?.server?.port && (
                          <TextWithLabel title="Puerto Api">
                            {data?.faceServer?.config?.server?.port}
                          </TextWithLabel>
                        )}
                        {data?.faceServer?.path && (
                          <TextWithLabel title="Path">{data?.faceServer?.path}</TextWithLabel>
                        )}
                        {data?.faceServer?.envConfigPath && (
                          <TextWithLabel title="Config Path">
                            {data?.faceServer?.envConfigPath}
                          </TextWithLabel>
                        )}
                        {data?.faceServer?.pathRoot && (
                          <TextWithLabel title="Path root">
                            {data?.faceServer?.pathRoot}
                          </TextWithLabel>
                        )}
                        {data?.faceServer?.config && (
                          <SnippetCopier component="pre" className="language-json">
                            {JSON?.stringify(data?.faceServer?.config, null, 2)}
                          </SnippetCopier>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} md={1} className="flex justify-center items-center">
                      <Divider orientation="vertical" flexItem />
                    </Grid>
                    {data?.fingerprintServer &&
                      Object?.keys(data?.fingerprintServer)?.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Divider textAlign="left" className="my-16">
                            <Typography className="text-2xl font-light">
                              Fingerprint Service Api
                            </Typography>
                          </Divider>
                          {data?.fingerprintServer?.name && (
                            <TextWithLabel title="Nombre">
                              {data?.fingerprintServer?.name}
                            </TextWithLabel>
                          )}
                          {data?.fingerprintServer?.version && (
                            <TextWithLabel title="Versión">
                              {data?.fingerprintServer?.version}
                            </TextWithLabel>
                          )}
                          {data?.fingerprintServer?.config?.server?.port && (
                            <TextWithLabel title="Puerto Api">
                              {data?.fingerprintServer?.config?.server?.port}
                            </TextWithLabel>
                          )}
                          {data?.fingerprintServer?.envConfigPath && (
                            <TextWithLabel title="Config Path">
                              {data?.fingerprintServer?.envConfigPath}
                            </TextWithLabel>
                          )}
                          {data?.fingerprintServer?.pathRoot && (
                            <TextWithLabel title="Path root">
                              {data?.fingerprintServer?.pathRoot}
                            </TextWithLabel>
                          )}
                          {data?.fingerprintServer?.config && (
                            <SnippetCopier component="pre" className="language-json">
                              {JSON?.stringify(data?.fingerprintServer?.config, null, 2)}
                            </SnippetCopier>
                          )}
                        </Grid>
                      )}
                    {data?.mqtt && (
                      <Grid xs={6} item>
                        <Divider textAlign="left" className="my-16">
                          <Typography className="text-2xl font-light">MQTT</Typography>
                        </Divider>
                        {data?.mqtt?.host && (
                          <TextWithLabel title="Host">{data?.mqtt?.host}</TextWithLabel>
                        )}
                        {data?.mqtt?.port && (
                          <TextWithLabel title="Port">{data?.mqtt?.port}</TextWithLabel>
                        )}
                        {data?.mqtt?.username && (
                          <TextWithLabel title="Usuario">{data?.mqtt?.username}</TextWithLabel>
                        )}
                        {data?.mqtt?.password && (
                          <TextWithLabel title="Constrasena">{data?.mqtt?.password}</TextWithLabel>
                        )}
                        {data?.mqtt?.publish && (
                          <TextWithLabel title="Publish Topic">{data?.mqtt?.publish}</TextWithLabel>
                        )}
                        {data?.mqtt?.subscribe && (
                          <TextWithLabel title="Subscribe Topic">
                            {data?.mqtt?.subscribe}
                          </TextWithLabel>
                        )}
                        {data?.mqtt?.qos && (
                          <TextWithLabel title="QoS">{data?.mqtt?.qos}</TextWithLabel>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </ComposedTags.Element>
              )}

              {/* Tab 4: Database */}
              {((data?.mongoDB && Object?.keys(data?.mongoDB)?.length > 0) ||
                (data?.redis && Object?.keys(data?.redis)?.length > 0)) && (
                <ComposedTags.Element index={4}>
                  <Grid spacing={8} container>
                    <Grid xs={6} item>
                      <Divider textAlign="left" className="my-16">
                        <Typography className="text-2xl font-light">MongoDB</Typography>
                      </Divider>
                      {data?.mongoDB?.connection && (
                        <TextWithLabel title="Conexión Key">
                          {data?.mongoDB?.connection}
                        </TextWithLabel>
                      )}
                      {data?.mongoDB?.type && (
                        <TextWithLabel title="Type Database">{data?.mongoDB?.type}</TextWithLabel>
                      )}
                      {data?.mongoDB?.host && (
                        <TextWithLabel title="Host">{data?.mongoDB?.host}</TextWithLabel>
                      )}
                      {data?.mongoDB?.port && (
                        <TextWithLabel title="Puerto">{data?.mongoDB?.port}</TextWithLabel>
                      )}
                      {data?.mongoDB?.database && (
                        <TextWithLabel title="Nombre de la base de datos">
                          {data?.mongoDB?.database}
                        </TextWithLabel>
                      )}
                      {data?.mongoDB?.username && (
                        <TextWithLabel title="Nombre de usuario">
                          {data?.mongoDB?.username}
                        </TextWithLabel>
                      )}
                      {data?.mongoDB?.password && (
                        <TextWithLabel title="Contraseña">{data?.mongoDB?.password}</TextWithLabel>
                      )}
                    </Grid>
                    <Grid xs={6} item>
                      <Divider textAlign="left" className="my-16">
                        <Typography className="text-2xl font-light">Redis</Typography>
                      </Divider>
                      {data?.redis?.host && (
                        <TextWithLabel title="Host">{data?.redis?.host}</TextWithLabel>
                      )}
                      {data?.redis?.port && (
                        <TextWithLabel title="Port">{data?.redis?.port}</TextWithLabel>
                      )}
                      {data?.redis?.username && (
                        <TextWithLabel title="Usuario">{data?.redis?.username}</TextWithLabel>
                      )}
                      {data?.redis?.password && (
                        <TextWithLabel title="Constrasena">{data?.redis?.password}</TextWithLabel>
                      )}
                      {data?.redis?.cacheTtlSeconds && (
                        <TextWithLabel title="Cache Ttl">
                          {data?.redis?.cacheTtlSeconds}s
                        </TextWithLabel>
                      )}
                      {data?.redis?.maxElementsForList && (
                        <TextWithLabel title="Elementos Por Lista">
                          {data?.redis?.maxElementsForList}
                        </TextWithLabel>
                      )}
                      {data?.redis?.cacheTtlSecondsForList && (
                        <TextWithLabel title="Cache Ttl Por Lista">
                          {data?.redis?.cacheTtlSecondsForList}s
                        </TextWithLabel>
                      )}
                    </Grid>
                  </Grid>
                </ComposedTags.Element>
              )}

              {/* Tab 5: GitHub */}
              {data?.github && Object?.keys(data?.github)?.length > 0 && (
                <ComposedTags.Element index={5}>
                  <Divider className="mb-16">
                    <Typography className="text-2xl font-light">API</Typography>
                  </Divider>
                  {data?.github?.api && (
                    <TextWithLabel title="Id">{data?.github?.api?.id}</TextWithLabel>
                  )}
                  {data?.github?.api && (
                    <TextWithLabel title="Nombre">{data?.github?.api?.name}</TextWithLabel>
                  )}
                  {data?.github?.api && (
                    <TextWithLabel title="URL">{data?.github?.api?.html_url}</TextWithLabel>
                  )}
                  {data?.github?.api && (
                    <TextWithLabel title="Tag">{data?.github?.api?.tag}</TextWithLabel>
                  )}

                  <Divider className="mb-16">
                    <Typography className="text-2xl font-light">Manager</Typography>
                  </Divider>
                  {data?.github?.manager && (
                    <TextWithLabel title="ID">{data?.github?.manager?.id}</TextWithLabel>
                  )}
                  {data?.github?.manager && (
                    <TextWithLabel title="Nombre">{data?.github?.manager?.name}</TextWithLabel>
                  )}
                  {data?.github?.manager && (
                    <TextWithLabel title="URL">{data?.github?.manager?.html_url}</TextWithLabel>
                  )}
                  {data?.github?.manager && (
                    <TextWithLabel title="Tag">{data?.github?.manager?.tag}</TextWithLabel>
                  )}
                  <Divider className="mb-16">
                    <Typography className="text-2xl font-light">Socket Notify</Typography>
                  </Divider>
                  {data?.github?.notify && (
                    <TextWithLabel title="ID">{data?.github?.notify?.id}</TextWithLabel>
                  )}
                  {data?.github?.notify && (
                    <TextWithLabel title="Nombre">{data?.github?.notify?.name}</TextWithLabel>
                  )}
                  {data?.github?.notify && (
                    <TextWithLabel title="URL">{data?.github?.notify?.html_url}</TextWithLabel>
                  )}
                  {data?.github?.notify && (
                    <TextWithLabel title="Tag">{data?.github?.notify?.tag}</TextWithLabel>
                  )}
                  <Divider className="mb-16">
                    <Typography className="text-2xl font-light">Socket Nest</Typography>
                  </Divider>
                  {data?.github?.websocket && (
                    <TextWithLabel title="ID">{data?.github?.websocket?.id}</TextWithLabel>
                  )}
                  {data?.github?.websocket && (
                    <TextWithLabel title="Nombre">{data?.github?.websocket?.name}</TextWithLabel>
                  )}
                  {data?.github?.websocket && (
                    <TextWithLabel title="URL">{data?.github?.websocket?.html_url}</TextWithLabel>
                  )}
                  {data?.github?.websocket && (
                    <TextWithLabel title="Tag">{data?.github?.websocket?.tag}</TextWithLabel>
                  )}
                  <Divider className="mb-16">
                    <Typography className="text-2xl font-light">Face Recognition Server</Typography>
                  </Divider>
                  {data?.github?.['face-recognition-server'] && (
                    <TextWithLabel title="ID">
                      {data?.github?.['face-recognition-server']?.id}
                    </TextWithLabel>
                  )}
                  {data?.github?.['face-recognition-server'] && (
                    <TextWithLabel title="Nombre">
                      {data?.github?.['face-recognition-server']?.name}
                    </TextWithLabel>
                  )}
                  {data?.github?.['face-recognition-server'] && (
                    <TextWithLabel title="URL">
                      {data?.github?.['face-recognition-server']?.html_url}
                    </TextWithLabel>
                  )}
                  {data?.github?.['face-recognition-server'] && (
                    <TextWithLabel title="Tag">
                      {data?.github?.['face-recognition-server']?.tag}
                    </TextWithLabel>
                  )}
                  <Divider className="mb-16">
                    <Typography className="text-2xl font-light">
                      Fingerprint Recognition Server
                    </Typography>
                  </Divider>
                  {data?.github?.['fingerprint-recognition-server'] && (
                    <TextWithLabel title="ID">
                      {data?.github?.['fingerprint-recognition-server']?.id}
                    </TextWithLabel>
                  )}
                  {data?.github?.['fingerprint-recognition-server'] && (
                    <TextWithLabel title="Nombre">
                      {data?.github?.['fingerprint-recognition-server']?.name}
                    </TextWithLabel>
                  )}
                  {data?.github?.['fingerprint-recognition-server'] && (
                    <TextWithLabel title="URL">
                      {data?.github?.['fingerprint-recognition-server']?.html_url}
                    </TextWithLabel>
                  )}
                  {data?.github?.['fingerprint-recognition-server'] && (
                    <TextWithLabel title="Tag">
                      {data?.github?.['fingerprint-recognition-server']?.tag}
                    </TextWithLabel>
                  )}
                </ComposedTags.Element>
              )}

              {/* Tab 6: Token/Auth Client */}
              {data?.token && Object?.keys(data?.token)?.length > 0 && (
                <ComposedTags.Element index={6}>
                  <Divider textAlign="left" className="my-16">
                    <Typography className="text-2xl font-light">Auth Client</Typography>
                  </Divider>
                  <FormCardLayout.FullColum>
                    {data && data?.token && data?.apiClient?.clientId && (
                      <TextWithLabel title="Client ID">{data?.apiClient?.clientId}</TextWithLabel>
                    )}
                    {data && data?.token && data?.apiClient?.clientSecret && (
                      <TextWithLabel title="Client Secret">
                        {data?.apiClient?.clientSecret}
                      </TextWithLabel>
                    )}
                    {data && data?.token && data?.token?.token_type && (
                      <TextWithLabel title="Tipo de token">{data?.token?.token_type}</TextWithLabel>
                    )}
                    {data && data?.token && data?.token?.expires && (
                      <TextWithLabel title="Fecha de expiracion">
                        {data?.token?.expires}
                      </TextWithLabel>
                    )}
                    {data && data?.token && data?.token?.access_token && (
                      <SnippetCopier component="pre" className="language-json">
                        {data?.token?.access_token}
                      </SnippetCopier>
                    )}
                  </FormCardLayout.FullColum>
                </ComposedTags.Element>
              )}
            </ComposedTags>
          </FormCardLayout>
        </Box>
      }
    />
  );
};
